import { createElement, useCallback, useMemo, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import type { ColorByCategoryGroup } from 'components/color-by-attribute-modal';
import { orderedBounds } from '../filter-modal/value-histogram';
import { allDatasetAtoms } from '../atoms';
import {
  NO_SUBSURFACE_FACETS,
  NO_SUBSURFACE_SEARCH,
  subsurfaceOpacityPercentAtom,
  subsurfaceColorRuleAtom,
} from './atoms';
import { newColorRule, type SubsurfaceColorRule } from './color-rule';
import { SUBSURFACE_COLOR_GROUP_ID, SubsurfaceColorForm } from './color-tab';
import { pooledRangeOf } from './utils';
import type { SubsurfaceMapRow } from './types';

const datasetAtoms = allDatasetAtoms.subsurfaceMaps;

type SubsurfaceSearch = { data?: { results?: SubsurfaceMapRow[] } };
type SubsurfaceFacets = {
  valueHistogram?: { min: number; max: number };
} | null;

/**
 * The listed maps and their pooled value range. Unsubscribed, it reads empty
 * stand-ins, so a closed modal doesn't re-render on every search.
 * @param {boolean} subscribed Whether to follow the Subsurface search.
 * @return {{ maps: SubsurfaceMapRow[], pooledRange: [number, number] }} Both.
 */
export function useSubsurfaceMaps(subscribed: boolean) {
  const search = useAtomValue(
    subscribed ? datasetAtoms.searchAtom : NO_SUBSURFACE_SEARCH
  ) as SubsurfaceSearch;
  const facets = useAtomValue(
    subscribed ? datasetAtoms.committedFacetsAtom : NO_SUBSURFACE_FACETS
  ) as SubsurfaceFacets;
  const maps = useMemo(() => search.data?.results ?? [], [search.data]);
  const pooledRange = useMemo(
    () => pooledRangeOf(maps, facets?.valueHistogram),
    [maps, facets]
  );
  return { maps, pooledRange };
}

/**
 * Reads the pooled range when called (e.g. in an event handler) without
 * subscribing the caller to the Subsurface search.
 * @return {Function} Returns the current [min, max].
 */
export function useReadPooledRange(): () => [number, number] {
  return useAtomCallback(
    useCallback((get) => {
      const search = get(datasetAtoms.searchAtom) as SubsurfaceSearch;
      const facets = get(datasetAtoms.committedFacetsAtom) as SubsurfaceFacets;
      return pooledRangeOf(search.data?.results ?? [], facets?.valueHistogram);
    }, [])
  );
}

/**
 * The Subsurface Maps tab of the shared Color by Attribute modal: a draft that starts
 * from the applied rule on every opening; Apply writes the rule and transparency.
 * @param {boolean} open Whether the modal is open.
 * @return {ColorByCategoryGroup} The tab, with its form and Apply.
 */
export function useSubsurfaceColorGroup(open: boolean): ColorByCategoryGroup {
  const { maps, pooledRange } = useSubsurfaceMaps(open);
  const [rule, setRule] = useAtom(subsurfaceColorRuleAtom);
  const [opacityPercent, setOpacityPercent] = useAtom(
    subsurfaceOpacityPercentAtom
  );
  const [draft, setDraft] = useState<SubsurfaceColorRule>(
    () => rule ?? newColorRule(pooledRange)
  );
  const [transparency, setTransparency] = useState(100 - opacityPercent);
  // Untouched since opening: Apply then leaves "no rule" alone, so each map keeps
  // its own default ramp instead of taking the draft's Viridis.
  const [edited, setEdited] = useState(false);

  // Start every opening from what is applied. Adjusted while rendering, not in an
  // effect, so the tab never shows the last draft first.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDraft(rule ?? newColorRule(pooledRange));
      setTransparency(100 - opacityPercent);
      setEdited(false);
    }
  }

  const updateDraft = useCallback((patch: Partial<SubsurfaceColorRule>) => {
    setDraft((current) => ({ ...current, ...patch }));
    setEdited(true);
  }, []);
  const units = maps[0]?.units ?? '';

  return useMemo(() => {
    const apply = () => {
      // A range typed back to front applies as the span between its ends.
      const [rangeMin, rangeMax] = orderedBounds(
        draft.rangeMin,
        draft.rangeMax
      );
      if (rule || edited) setRule({ ...draft, rangeMin, rangeMax });
      setOpacityPercent(100 - transparency);
    };
    return {
      id: SUBSURFACE_COLOR_GROUP_ID,
      label: 'Petrotech Maps',
      categories: [{ id: 'default', label: 'Default Values' }],
      panel: {
        content: createElement(SubsurfaceColorForm, {
          draft,
          units,
          transparency,
          onTransparencyChange: setTransparency,
          onChange: updateDraft,
        }),
        onApply: apply,
      },
    };
  }, [
    draft,
    units,
    transparency,
    updateDraft,
    rule,
    edited,
    setRule,
    setOpacityPercent,
  ]);
}
